import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { READINESS_META } from "@/config/menu-guideline-status";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";

// Custom SVG flow renderer (AC3.7 — LOCKED no React Flow / D3 / Cytoscape).
// Two layouts:
//   - "flow": numbered phase cards in a row, arrows between
//   - "hub":  center node + N sources flowing in + N reports flowing out
//
// Card dimensions are deliberately fixed so arrow geometry is deterministic.
const CARD_W = 200;
const CARD_H = 120;
const GAP = 56;       // gap between cards = arrow length
const ROW_PADDING_Y = 8;

function arrowMarkerId(kind) { return kind === "primary" ? "arrow-primary" : "arrow-optional"; }

function ArrowDefs() {
  return (
    <defs>
      <marker id="arrow-primary" viewBox="0 0 10 10" refX="9" refY="5"
        markerWidth="6" markerHeight="6" orient="auto-start-reverse">
        <path d="M 0 0 L 10 5 L 0 10 z" fill="#047857" />
      </marker>
      <marker id="arrow-optional" viewBox="0 0 10 10" refX="9" refY="5"
        markerWidth="6" markerHeight="6" orient="auto-start-reverse">
        <path d="M 0 0 L 10 5 L 0 10 z" fill="#a8a29e" />
      </marker>
    </defs>
  );
}

function PhaseCard({ phase, x, y, onOpen }) {
  const r = READINESS_META[phase.readiness || "ready"];
  const isDisabled = phase.readiness === "disabled";
  const isBeta = phase.readiness === "beta";
  const isPartial = phase.readiness === "partial";
  const clickable = !isDisabled;

  const fill = isDisabled ? "#fafaf9" : "#ffffff";
  const stroke = isDisabled ? "#e7e5e4" : isBeta ? "#eab308" : isPartial ? "#f59e0b" : "#10b981";
  const opacity = isDisabled ? 0.55 : 1;

  return (
    <g
      transform={`translate(${x}, ${y})`}
      opacity={opacity}
      style={{ cursor: clickable && (phase.route || onOpen) ? "pointer" : "default" }}
      onClick={() => clickable && onOpen && onOpen(phase)}
      data-testid={`phase-card-${phase.id}`}
    >
      <rect width={CARD_W} height={CARD_H} rx="6" ry="6"
            fill={fill} stroke={stroke} strokeWidth="1.5" />
      {/* Number chip */}
      <g transform="translate(12, 12)">
        <rect width="36" height="22" rx="3" ry="3"
              fill={isDisabled ? "#e7e5e4" : "#0f2a22"} />
        <text x="18" y="15" textAnchor="middle"
              fontSize="11" fontWeight="700"
              fontFamily="ui-sans-serif, system-ui"
              fill={isDisabled ? "#78716c" : "#fcd34d"}>
          {phase.no}
        </text>
      </g>
      {/* Status pill */}
      <g transform={`translate(${CARD_W - 70}, 12)`}>
        <rect width="58" height="20" rx="10" ry="10"
              fill={isDisabled ? "#e7e5e4" :
                    isBeta ? "#fef9c3" :
                    isPartial ? "#fef3c7" : "#d1fae5"} />
        <text x="29" y="14" textAnchor="middle"
              fontSize="10" fontWeight="600"
              fontFamily="ui-sans-serif, system-ui"
              fill={isDisabled ? "#57534e" :
                    isBeta ? "#854d0e" :
                    isPartial ? "#92400e" : "#065f46"}>
          {r.label}
        </text>
      </g>
      {/* Title */}
      <text x="12" y="60"
            fontSize="16" fontWeight="700"
            fontFamily="ui-serif, Georgia, serif"
            fill={isDisabled ? "#78716c" : "#1c1917"}>
        {phase.title}
      </text>
      {/* Subtitle */}
      <text x="12" y="80"
            fontSize="11"
            fontFamily="ui-sans-serif, system-ui"
            fill={isDisabled ? "#a8a29e" : "#57534e"}>
        {(phase.subtitle || "").slice(0, 32)}
      </text>
      {/* Sub-items (max 2 visible) */}
      {phase.subItems && phase.subItems.slice(0, 2).map((s, idx) => (
        <g key={idx} transform={`translate(12, ${94 + idx * 12})`}>
          <circle cx="3" cy="-3" r="2.5"
                  fill={s.state === "disabled" ? "#d6d3d1" : "#10b981"} />
          <text x="10" y="0"
                fontSize="10"
                fontFamily="ui-sans-serif, system-ui"
                fill={s.state === "disabled" ? "#a8a29e" : "#44403c"}>
            {s.label}{s.optional ? " · optional" : ""}
          </text>
        </g>
      ))}
    </g>
  );
}

function FlowLayout({ tab, onOpenPhase }) {
  const phases = tab.phases || [];
  const totalW = phases.length * CARD_W + (phases.length - 1) * GAP + 32;
  const totalH = CARD_H + ROW_PADDING_Y * 2 + 32;

  const positions = useMemo(() => {
    const m = new Map();
    phases.forEach((p, i) => {
      m.set(p.id, { x: 16 + i * (CARD_W + GAP), y: ROW_PADDING_Y + 16 });
    });
    return m;
  }, [phases]);

  return (
    <svg viewBox={`0 0 ${totalW} ${totalH}`} className="w-full h-auto"
         style={{ minWidth: totalW, maxWidth: "100%" }}>
      <ArrowDefs />
      {/* Arrows behind cards */}
      {(tab.arrows || []).map((a, idx) => {
        const from = positions.get(a.from);
        const to = positions.get(a.to);
        if (!from || !to) return null;
        const x1 = from.x + CARD_W;
        const y1 = from.y + CARD_H / 2;
        const x2 = to.x;
        const y2 = to.y + CARD_H / 2;
        const isPrimary = a.kind === "primary";
        return (
          <line key={idx}
            x1={x1} y1={y1} x2={x2 - 4} y2={y2}
            stroke={isPrimary ? "#047857" : "#a8a29e"}
            strokeWidth="2"
            strokeDasharray={isPrimary ? undefined : "5 4"}
            markerEnd={`url(#${arrowMarkerId(a.kind)})`} />
        );
      })}
      {phases.map((p) => {
        const pos = positions.get(p.id);
        return <PhaseCard key={p.id} phase={p} x={pos.x} y={pos.y} onOpen={onOpenPhase} />;
      })}
    </svg>
  );
}

function HubNode({ x, y, title, subtitle, tone = "stone", radius = 70, isCenter = false }) {
  const palette = {
    emerald: { fill: "#d1fae5", stroke: "#10b981", text: "#065f46" },
    amber:   { fill: "#fef3c7", stroke: "#f59e0b", text: "#92400e" },
    rose:    { fill: "#ffe4e6", stroke: "#f43f5e", text: "#9f1239" },
    blue:    { fill: "#dbeafe", stroke: "#3b82f6", text: "#1e40af" },
    violet:  { fill: "#ede9fe", stroke: "#8b5cf6", text: "#5b21b6" },
    stone:   { fill: "#f5f5f4", stroke: "#a8a29e", text: "#1c1917" },
  };
  const p = palette[tone] || palette.stone;
  if (isCenter) {
    return (
      <g transform={`translate(${x}, ${y})`}>
        <circle r={radius} fill="#0f2a22" stroke="#fcd34d" strokeWidth="3" />
        <text textAnchor="middle" y="-4"
              fontSize="14" fontWeight="700"
              fontFamily="ui-serif, Georgia, serif"
              fill="#fcd34d">{title}</text>
        <text textAnchor="middle" y="14"
              fontSize="10" fill="#d6d3d1">{subtitle}</text>
      </g>
    );
  }
  return (
    <g transform={`translate(${x}, ${y})`}>
      <circle r="6" fill={p.stroke} />
      <text x="12" y="4" fontSize="11" fontWeight="600" fill={p.text}
            fontFamily="ui-sans-serif, system-ui">{title}</text>
    </g>
  );
}

function HubLayout({ tab }) {
  const sources = tab.sources || [];
  const reports = tab.reports || [];
  const W = 880, H = 360;
  const cx = W / 2, cy = H / 2;
  const radiusOut = 280;

  const sourcePoints = sources.map((s, i) => {
    const t = sources.length === 1 ? 0.5 : i / (sources.length - 1);
    return { id: s.id, ...s, x: 30, y: 30 + t * (H - 60) };
  });
  const reportPoints = reports.map((r, i) => {
    const t = reports.length === 1 ? 0.5 : i / (reports.length - 1);
    return { id: r.id, ...r, x: W - 30, y: 30 + t * (H - 60) };
  });

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ maxWidth: "100%" }}>
      <ArrowDefs />
      {/* lines source -> center */}
      {sourcePoints.map((s) => (
        <line key={`s-${s.id}`} x1={s.x + 8} y1={s.y} x2={cx - 72} y2={cy}
              stroke="#a8a29e" strokeWidth="1" strokeDasharray="3 3"
              opacity="0.6" />
      ))}
      {/* lines center -> report */}
      {reportPoints.map((r) => (
        <line key={`r-${r.id}`} x1={cx + 72} y1={cy} x2={r.x - 8} y2={r.y}
              stroke="#a8a29e" strokeWidth="1" strokeDasharray="3 3"
              opacity="0.6" />
      ))}
      <HubNode x={cx} y={cy} title={tab.centerNode.title} subtitle={tab.centerNode.subtitle} isCenter />
      {sourcePoints.map((s) => <HubNode key={s.id} {...s} />)}
      {reportPoints.map((r) => <HubNode key={r.id} {...r} />)}
      <text x="30" y="20" fontSize="10" fontWeight="700"
            fill="#57534e" fontFamily="ui-sans-serif, system-ui"
            letterSpacing="2">SOURCES</text>
      <text x={W - 30} y="20" textAnchor="end"
            fontSize="10" fontWeight="700"
            fill="#57534e" fontFamily="ui-sans-serif, system-ui"
            letterSpacing="2">REPORTS</text>
    </svg>
  );
}

export default function FlowMap({ tab, onOpenPhase }) {
  const nav = useNavigate();

  const handleOpen = (phase) => {
    if (onOpenPhase) return onOpenPhase(phase);
    if (phase.route && phase.readiness !== "disabled") nav(phase.route);
  };

  if (!tab) return null;

  if (tab.readiness === "disabled" && tab.layout !== "hub") {
    // Render normally but the whole tab gets dim opacity via overlay.
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="w-full overflow-x-auto" data-testid={`flow-map-${tab.key}`}>
        {tab.layout === "hub"
          ? <HubLayout tab={tab} />
          : <FlowLayout tab={tab} onOpenPhase={handleOpen} />}
      </div>
      {tab.layout === "flow" && tab.phases && (
        <div className="mt-3 flex flex-wrap gap-2 text-xs text-stone-500">
          {tab.phases.filter(p => p.readiness === "disabled").map(p => (
            <Tooltip key={p.id}>
              <TooltipTrigger asChild>
                <Badge variant="outline" className="border-stone-300 text-stone-500 font-normal">
                  {p.no} {p.title} · Sắp ra mắt
                </Badge>
              </TooltipTrigger>
              <TooltipContent>Liên hệ admin để biết lộ trình</TooltipContent>
            </Tooltip>
          ))}
        </div>
      )}
    </TooltipProvider>
  );
}
